<template>
  <div>
    <v-table class="vendor-table">
      <thead>
        <tr>
          <th>VENDOR</th>
          <th>CITY</th>
          <th>CONTACT</th>
          <th>PHONE</th>
          <th>RESPONDED</th>
          <th>QUOTE STATUS</th>
          <th>PRICE</th>
          <th />
        </tr>
      </thead>
      <tbody>
        <tr v-for="quote in order.quotes" :key="quote.id">
          <td class="font-weight-bold">
            {{ quote.vendor?.name }}
          </td>
          <td>{{ quote.vendor?.city }}</td>
          <td>
            <span :class="{ 'text-mid-light-grey': !quote.salesperson }">
              {{ salespersonName(quote.salesperson) }}
            </span>
            <br>
            <span class="vendor-contact-email">
              {{ salespersonEmail(quote.salesperson) }}
            </span>
          </td>
          <td>{{ salespersonPhoneNumber(quote.salesperson) }}</td>
          <td class="text-center">
            <quote-status-icon :quote="quote" />
          </td>
          <td>
            <quote-outdated-chip :quote="quote" />
          </td>
          <td>
            <span :class="{ 'text-mid-light-grey': quote.total_price == null }">{{ currency(quote.total_price) }}</span>
          </td>
          <td>
            <qtm-btn tertiary @click="editQuote(quote)">
              Edit
            </qtm-btn>
          </td>
        </tr>
      </tbody>
    </v-table>
    <enter-quote-dialog
      v-model="showQuote"
      :quote="activeQuote"
      :order="order"
      @update:model-value="activeQuote = undefined"
    />
  </div>
</template>

<script setup lang="ts">
import cloneDeep from 'lodash.clonedeep'
import { currency } from '~/models/filters'
import EnterQuoteDialog from '@/components/quotes/enter-quote-dialog.vue'
import QuoteOutdatedChip from '@/components/quotes/quote-outdated-chip.vue'
import QuoteStatusIcon from '@/components/admin/quote-status-icon.vue'
import type { Contact, Order, Quote } from '@quotetome/materials-api'

interface Props {
  order: Order
}
defineProps<Props>()

const activeQuote = ref()
const showQuote = ref(false)

const editQuote = (quote: Quote) => {
  // This object is cached in the user's browser, and won't be re-fetched from the backend unless they reload.
  // Clone so the user can cancel and throw away any changes without having to refresh the page or object.
  activeQuote.value = cloneDeep(quote)

  // Modifying the attachments is a separate event from modifying the quote and cannot be reversed.
  // Setting them here reflects that in the cached data.
  activeQuote.value.attachments = quote.attachments
  showQuote.value = true
}

const salespersonEmail = (salesperson: Contact) => {
  return salesperson ? salesperson.email : ''
}

const salespersonName = (salesperson: Contact) => {
  return salesperson ? `${salesperson.first_name} ${salesperson.last_name}` : '—'
}

const salespersonPhoneNumber = (salesperson: Contact) => {
  const phone = salesperson?.phone_number || ''

  if (salesperson?.phone_extension) {
    return `${phone} ext. ${salesperson.phone_extension}`
  }

  return phone
}
</script>

<style lang="scss" scoped>
.vendor-table thead {
  background-color: rgb(var(--v-theme-background));
  text-align: left;
}

.vendor-table th {
  padding: 0.25rem 16px;
  font-weight: normal;
}

.vendor-contact-email {
  color: rgba(83, 83, 83, 0.6);
}
</style>
